import sortBy from 'lodash/sortBy';
import get from 'lodash/get';
import {
  getItemOrDefaultToValue,
  getPersonName,
  getElapsedTime,
  getValueFromConstantObject,
} from 'utils/details';
import { displayTime } from 'utils/time';
import {
  getDistressResponseStatus,
  getDistressResponseStatusId,
} from 'utils/helper/distress';
import { getDisplayRoles, getRoleId } from 'utils/helper/roles';
import { DISTRESS_RESPONSE_STATUS } from 'utils/constants/distressStatus';
import { LIST_WIDTH } from 'utils/constants/listview';

export const arrangeByTime = items => {
  const lengthOfItems = items.length;
  return sortBy(items, 'timestamp').map((item, key) => {
    const lastItem = key === lengthOfItems - 1;

    return {
      ...item,
      latest: lastItem,
    };
  });
};
const transformResponder = responder => {
  if (!responder)
    return {
      responder: {
        type: 'text',
        value: '-',
      },
    };
  return {
    employeeId: {
      type: 'text',
      label: 'Employee ID',
      value: getItemOrDefaultToValue(responder.general, 'employeeId', '-'),
    },
    responderName: {
      type: 'text',
      value: getPersonName(responder.general),
    },
    responderRole: {
      type: 'text',
      value: getDisplayRoles(responder.general, 'roles'),
      idValue: getRoleId(responder.general),
    },
  };
};
export const transformDistressDetails = ({ details, current, timeZone }) => {
  return {
    generalInfo: {
      distressId: { type: 'text', value: get(details, 'ref') },
      createdAt: {
        type: 'text',
        value: displayTime({
          timestamp: getItemOrDefaultToValue(details, 'currentStatusTimestamp'),
          timeZone,
        }),
      },
      elapsedTime: {
        type: 'text',
        value: getElapsedTime(
          {
            currentStatus: details.currentStatus,
            startTime: details.statusAudit.find(
              key => key.status === DISTRESS_RESPONSE_STATUS.NEW.id,
            ).timestamp,
            currentStatusTimestamp: details.currentStatusTimestamp,
          },
          current,
        ),
      },
      initiatorName: {
        type: 'text',
        value: getPersonName(details.distressee.general),
      },
      initiatorRole: {
        type: 'text',
        value: getDisplayRoles(details.distressee.general, 'roles'),
        idValue: getRoleId(details.distressee.general),
      },
      status: {
        type: 'text',
        value: getDistressResponseStatus(details),
        idValue: getDistressResponseStatusId(details),
      },
    },
    //detailed panel
    detailedInfo: {
      //responder:
      ...transformResponder(details.responder),
      auditStatus: {
        type: 'list-data',
        value: {
          data: details.statusAudit.map(s => {
            return {
              status: getValueFromConstantObject({
                constant: DISTRESS_RESPONSE_STATUS,
                value: s.status,
                key: 'name',
              }),
              timestamp: displayTime({
                timestamp: getItemOrDefaultToValue(s, 'timestamp'),
                timeZone,
              }),
            };
          }),
          columns: [
            { headerName: 'status', field: 'status', width: LIST_WIDTH.xl },
            { headerName: 'time', field: 'timestamp', width: LIST_WIDTH.xl },
          ],
        },
      },
    },
    others: {
      image: {
        value: getItemOrDefaultToValue(details.distressee.general, 'photoUrl'),
      },
      distressee_role:
        details.distressee != null &&
        getDisplayRoles(details.distressee.general, 'roles'),
      distressee_location:
        details.distressee != null &&
        details.distressee.operational.location != null &&
        getItemOrDefaultToValue(
          details.distressee.operational.location,
          'point',
        ),
      distressee:
        details.distressee !== null
          ? {
              generalInfo: {
                role: {
                  type: 'text',
                  value: getDisplayRoles(details.distressee.general),
                },
                name: {
                  type: 'text',
                  value: getPersonName(details.distressee.general),
                },
                phone: {
                  type: 'text',
                  value:
                    details.distressee.general.phoneNumber !== null
                      ? `${getItemOrDefaultToValue(
                          details.distressee.general.phoneNumber,
                          'prefix',
                          '-',
                        )} ${getItemOrDefaultToValue(
                          details.distressee.general.phoneNumber,
                          'number',
                          '-',
                        )}`
                      : '-',
                },
                reliability: {
                  type: 'rating',
                  value: getItemOrDefaultToValue(
                    details.distressee.details,
                    'reliability',
                    0,
                  ),
                },
              },
              image: {
                value: getItemOrDefaultToValue(
                  details.distressee.general,
                  'photoUrl',
                ),
              },
              status: {
                value: getItemOrDefaultToValue(
                  details.distressee.operational,
                  'status',
                ),
              },
            }
          : undefined,
      responder_location:
        details.responder != null &&
        details.responder.operational.location != null &&
        getItemOrDefaultToValue(
          details.responder.operational.location,
          'point',
        ),
      responder:
        details.responder !== null
          ? {
              generalInfo: {
                role: {
                  type: 'text',
                  value: getDisplayRoles(details.responder.general),
                },
                name: {
                  type: 'text',
                  value: getPersonName(details.responder.general),
                },
                phone: {
                  type: 'text',
                  value:
                    details.responder !== null
                      ? `${getItemOrDefaultToValue(
                          details.responder.general.phoneNumber,
                          'prefix',
                          '-',
                        )} ${getItemOrDefaultToValue(
                          details.responder.general.phoneNumber,
                          'number',
                          '-',
                        )}`
                      : '-',
                },

                reliability: {
                  type: 'rating',
                  value: getItemOrDefaultToValue(
                    details.responder.details,
                    'reliability',
                    0,
                  ),
                },
              },
              image: {
                value: getItemOrDefaultToValue(
                  details.responder.general,
                  'photoUrl',
                ),
              },
              status: {
                value: getItemOrDefaultToValue(
                  details.responder.operational,
                  'status',
                ),
              },
              raw: {
                id: details.responder.general.id,
                label: getPersonName(details.responder.general),
                value: details.responder,
              },
            }
          : undefined,
      status: {
        value: arrangeByTime(getItemOrDefaultToValue(details, 'statusAudit')),
      },
      id: { value: get(details, 'id') },
      estimatedDistance: {
        value: get(details, 'estimatedDistance'),
      },
    },
  };
};
